import React, { useState, useEffect, useRef } from "react";
import "../CSS/CreateReportForm.css";
import { uploadFilesDirectToS3, attachMediaToReport, PresignUnavailableError } from "../services/s3Upload";
import {
  CheckCircle,
  Send,
  XCircle,
  AlertTriangle,
  RefreshCw,
  LockKeyhole,
  FileText,
} from "lucide-react";

// Redux stuff
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

// Thunks for reports
import {
  fetchReportThunkById,
  createReportThunk,
  updateReportThunk,
  fetchReportCommentsThunk,
  addReportCommentThunk,
  fetchReportTimelineThunk,
} from "../features/reports/reportThunks";

// Thunk for stores
import {
  fetchStoresThunk,
  fetchStoreNotesThunk
} from "../features/stores/storeThunks";

// Import employee thunk to fetch employee list
import { fetchEmployeesThunk } from "../features/employees/employeeThunks";

// ============ COMPRESSION IMPORTS ============
import imageCompression from 'browser-image-compression';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
// ==============================================

// ============ ROLE-BASED PERMISSIONS ============
// Role rank constants (MUST match backend permissions.py ROLE_RANKS)
const ROLE_RANKS = {
  // Global Admin - Highest Authority
  'ADMIN': 10,

  // Department Heads - Rank 9
  'OPS_HEAD_ADMIN': 9,
  'HR_HEAD': 9,
  'SALES_HEAD': 9,
  'TECH_ADMIN': 9,
  'CHIEF_DEVELOPMENT_HEAD': 9,
  'FL_ADMIN': 9,

  // Managers - Rank 7
  'MANAGER': 7,
  'HR_MANAGER': 7,
  'TECH_MANAGER': 7,
  'DEVELOPMENT_MANAGER': 7,
  'FLOOR_SUPERVISOR': 7,

  // Team Managers / Coordinators - Rank 5
  'TEAM_MANAGER': 5,
  'HR_COORDINATOR': 5,
  'FLOOR_COORDINATOR': 5,

  // Executives / Assistants - Rank 3
  'SALES_EXECUTIVE': 3,
  'TECH_EXECUTIVE': 3,
  'FRONTEND_BACKEND_EXECUTIVE': 3,
  'PAYROLL_ASSISTANT': 3,
  'TECH_ASSISTANT': 3,
  'FRONTEND_BACKEND_ASSISTANT': 3,
  'OFFICE_ASSISTANT': 3,

  // Sales Assistant - Rank 2
  'SALES_ASSISTANT': 2,

  // Common Roles
  'SPA': 2,
  'PA': 1,
};

// Helper: numeric rank for the logged-in user
const getUserRank = (user) => {
  if (!user || !user.employee_profile) return 0;
  return ROLE_RANKS[user.employee_profile.role] ?? 0;
};

// Helper: PA only (rank === 1)
const isPA = (user) => getUserRank(user) === 1;

// Helper: SPA / Sales Assistant and above (rank >= 2)
// Can submit, decline, mark discrepancy, update reports
const isSPAOrAbove = (user) => getUserRank(user) >= 2;

// Helper: Team Manager / Coordinator and above (rank >= 5)
// Can APPROVE reports
const isTeamManagerOrAbove = (user) => getUserRank(user) >= 5;
// ================================================

function CreateEditReportForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Are we editing or creating?
  const { reportId } = useParams();
  const editMode = Boolean(reportId);

  // Redux states
  const { reports, loading: reportsLoading } = useSelector(
    (state) => state.reports
  );
  const { stores, loading: storesLoading } = useSelector((state) => state.stores);
  const { employees, loading: employeesLoading } = useSelector(
    (state) => state.employees
  );
  const { user } = useSelector((state) => state.auth);

  // Store notes from Redux
  const storeNotes = useSelector((state) => state.stores.notesData.selectedStoreNotes);
  const notesLoading = useSelector((state) => state.stores.notesData.loading);

  // Comments from Redux
  const commentsState = useSelector((state) =>
    state.reports.comments.byReportId[reportId] || { comments: [], loading: false, error: null }
  );
  const { addingComment, addCommentError } = useSelector((state) => state.reports.comments);

  // Timeline from Redux
  const timelineState = useSelector((state) =>
    state.reports.timeline.byReportId[reportId] || { entries: [], loading: false, error: null, count: 0 }
  );

  // We assume user.employee_profile.id is the employee ID
  const employeeId = user?.employee_profile?.id;

  // Role-based permissions
  const userIsPA = isPA(user);
  const userIsSPAOrAbove = isSPAOrAbove(user);
  const userIsTeamManagerOrAbove = isTeamManagerOrAbove(user);
  const userRank = getUserRank(user);

  // Attempt to find the existing report in Redux
  const existingReport = editMode
    ? reports.find((r) => r.id === parseInt(reportId))
    : null;

  // Initial form state
  const initialFormData = {
    incident_date: "",
    incident_time: null,
    store: "",
    details: "",
    call_status: "",
    reason_called: "",
    reason_not_called: "",
    severity: "",
    announcement_by: null,
    customer_category: "",
    dress: "",
    product: "",
    pickup_time: null,
    hide_time: null,
    exit_time: null,
    status: "SUBMITTED_REVIEW",
    incident_type: "theftPrevented",
  };

  // Local form state
  const [formData, setFormData] = useState(initialFormData);

  // Separate states for existing and new files
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);

  // ============ NEW: Preview URLs for new files ============
  const [newFilePreviewUrls, setNewFilePreviewUrls] = useState([]);
  const newFilePreviewUrlsRef = useRef([]);
  // =========================================================

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  // Upload queue states - FIX for race condition
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);

  // FIX #3: Prevent duplicate report submissions
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Comment state
  const [newComment, setNewComment] = useState("");

  // Active tab for the Comments / Activity Log section
  const [activeTab, setActiveTab] = useState("comments"); // "comments" | "timeline"

  // NEW: Mention states
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionedEmployees, setMentionedEmployees] = useState([]); // Track mentioned employees with IDs
  const commentInputRef = useRef(null);

  // NEW: Store search and notes modal states
  const [storeSearch, setStoreSearch] = useState("");
  const [showStoreNotesModal, setShowStoreNotesModal] = useState(false);
  const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);

  // Modal state
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
    confirmAction: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  // Action menu state
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const actionMenuRef = useRef(null);
  const storeDropdownRef = useRef(null);
  const mentionDropdownRef = useRef(null);

  // FIX: Guard to ensure the populate effect only runs once per report
  // Prevents Redux re-renders from resetting user's file changes mid-session
  const hasInitializedFiles = useRef(false);

  // FIX: Track the active reportId so async video compression can detect stale results
  // when the user navigates away mid-compression
  const activeReportIdRef = useRef(reportId);

  // ============ COMPRESSION STATES ============
  const [ffmpeg] = useState(() => new FFmpeg());
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionStatus, setCompressionStatus] = useState('');
  // ============================================

  // Reason arrays
  const reasonCalledOptions = [
    "Able to stop",
    "Customer denied",
    "Cashier didn't ask the customer",
    "Customer left the store immediately",
    "Cashier Fraud",
    "Other",
  ];
  const reasonNotCalledOptions = [
    "Network issue",
    "Not sure about stealing",
    "Customer left the store immediately",
    "Cashier Suspicious Activity",
    "Cashier Opened the store",
    "Cashier Closed the store",
    "Other",
  ];

  // The store ID that was originally saved on the report (only relevant in edit mode)
  const originalStoreId = editMode ? (existingReport?.store?.id || null) : null;

  // Filter stores based on search:
  //   - Create mode  → active stores only
  //   - Edit mode    → active stores + the originally-selected store (even if now inactive)
  const filteredStores = stores.filter(store => {
    const matchesSearch =
      store?.store_name?.toLowerCase().includes(storeSearch.toLowerCase()) ||
      store?.store_city?.toLowerCase().includes(storeSearch.toLowerCase());

    if (!matchesSearch) return false;

    // Always include the originally-selected store in edit mode so it stays visible
    if (editMode && store.id === originalStoreId) return true;

    // Otherwise only include active stores
    return store.is_active !== false;
  });

  // Get selected store details
  const selectedStore = stores.find(s => s.id === formData.store);

  // NEW: Filter employees for mention dropdown
  const filteredEmployeesForMention = employees.filter(emp => {
    const fullName = (emp.user ? `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}` : emp.name || '').toLowerCase();
    const username = emp.user?.username?.toLowerCase() || '';
    const search = mentionSearch.toLowerCase();
    return fullName.includes(search) || username.includes(search);
  });

  // Modal functions
  const showModal = (type, title, message, confirmAction = null, confirmText = 'Confirm', cancelText = 'Cancel') => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      confirmAction,
      confirmText,
      cancelText
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleModalConfirm = () => {
    if (modalState.confirmAction) {
      modalState.confirmAction();
    }
    closeModal();
  };

  // NEW: Handle store notes modal
  const handleShowStoreNotes = () => {
    if (!formData.store) {
      showModal('error', 'Error', 'Please select a store first');
      return;
    }

    dispatch(fetchStoreNotesThunk(formData.store));
    setShowStoreNotesModal(true);
  };

  const handleCloseStoreNotesModal = () => {
    setShowStoreNotesModal(false);
  };

  // Helper functions
  const getStatusBadgeClass = (status) => {
    const statusLower = status.toLowerCase();
    return `incident-report-status-${statusLower}`;
  };

  const getFileNameFromUrl = (url) => {
    let name = url.split('/').pop().split('?')[0];
    // Presigned uploads (Aug 2026, first format) prefixed keys with a
    // 32-hex uuid: "9f3c...14_BBTS_theft_20260806.mp4". Strip it so the
    // team's readable convention shows in file cards. New uploads already
    // lead with the name (short uuid moved before the extension).
    name = name.replace(/^[0-9a-f]{32}_/i, '');
    return name;
  };

  const getFileTypeFromUrl = (url) => {
    const extension = url.split('.').pop().toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];
    const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'py', 'js', 'css', 'html'];

    if (imageExtensions.includes(extension)) return 'image';
    if (videoExtensions.includes(extension)) return 'video';
    if (documentExtensions.includes(extension)) return 'document';
    return 'document';
  };

  // NEW: Parse comment text to highlight mentions
  const parseCommentText = (text) => {
    const mentionPattern = /@\[([^\]]+)\]\((\d+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionPattern.exec(text)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }

      // Add mention
      parts.push({
        type: 'mention',
        name: match[1],
        id: match[2]
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // NEW: Handle comment input change with mention detection
  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;

    setNewComment(value);
    setCursorPosition(cursorPos);

    // Clean up mentioned employees list if mentions were removed from text
    setMentionedEmployees(prev => {
      return prev.filter(emp => value.includes(`@${emp.name}`));
    });

    // Check if user is typing a mention
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);

      // Check if there's a space after @ (which would end the mention)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionSearch(textAfterAt);
        setShowMentionDropdown(true);

        // Calculate dropdown position
        if (commentInputRef.current) {
          const textarea = commentInputRef.current;
          const textareaRect = textarea.getBoundingClientRect();

          // Approximate position (you might need to fine-tune this)
          setMentionPosition({
            top: textareaRect.bottom + window.scrollY,
            left: textareaRect.left + window.scrollX
          });
        }
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  // NEW: Handle employee mention selection - Clean display without brackets and IDs
  const handleMentionSelect = (employee) => {
    const textBeforeCursor = newComment.substring(0, cursorPosition);
    const textAfterCursor = newComment.substring(cursorPosition);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    const employeeName = (employee.user ? `${employee.user?.first_name || ''} ${employee.user?.last_name || ''}` : employee.name || '').trim();

    // Insert clean mention format: just @Name (no brackets or ID)
    const mention = `@${employeeName}`;

    const newText =
      newComment.substring(0, lastAtSymbol) +
      mention +
      ' ' +
      textAfterCursor;

    setNewComment(newText);

    // Store the mentioned employee with ID for later submission
    setMentionedEmployees(prev => {
      // Avoid duplicates
      if (!prev.find(emp => emp.id === employee.id)) {
        return [...prev, { id: employee.id, name: employeeName }];
      }
      return prev;
    });

    setShowMentionDropdown(false);
    setMentionSearch('');

    // Focus back on textarea
    if (commentInputRef.current) {
      commentInputRef.current.focus();
      const newCursorPos = lastAtSymbol + mention.length + 1;
      setTimeout(() => {
        commentInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActionMenuOpen(false);
      }
      if (storeDropdownRef.current && !storeDropdownRef.current.contains(event.target)) {
        setStoreDropdownOpen(false);
      }
      if (mentionDropdownRef.current && !mentionDropdownRef.current.contains(event.target)) {
        setShowMentionDropdown(false);
      }
    };

    if (actionMenuOpen || storeDropdownOpen || showMentionDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [actionMenuOpen, storeDropdownOpen, showMentionDropdown]);

  // ============ NEW: Keep a ref of preview URLs so we can clean them on unmount ============
  useEffect(() => {
    newFilePreviewUrlsRef.current = newFilePreviewUrls;
  }, [newFilePreviewUrls]);

  // ============ NEW: Cleanup preview URLs when component unmounts ============
  useEffect(() => {
    return () => {
      newFilePreviewUrlsRef.current.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);
  // ===========================================================================

  // Fetch stores and employees on mount
  useEffect(() => {
    dispatch(fetchStoresThunk());
    dispatch(fetchEmployeesThunk());
  }, [dispatch]);

  // ============ LOAD FFMPEG ON MOUNT ============
  useEffect(() => {
    const loadFFmpeg = async () => {
      try {
        const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';

        ffmpeg.on('log', ({ message }) => {
          console.log('FFmpeg:', message);
        });

        ffmpeg.on('progress', ({ progress }) => {
          const percent = Math.round(progress * 100);
          setCompressionProgress(percent);
        });

        await ffmpeg.load({
          coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });

        setFfmpegLoaded(true);
        console.log('✅ FFmpeg loaded successfully');
      } catch (error) {
        console.error('❌ FFmpeg loading error:', error);
        setFfmpegLoaded(false);
      }
    };

    loadFFmpeg();
  }, [ffmpeg]);
  // ===============================================

  // If edit mode, fetch the existing report if not in Redux
  useEffect(() => {
    if (editMode && !existingReport) {
      dispatch(fetchReportThunkById(reportId));
    }
  }, [editMode, existingReport, reportId, dispatch]);

  // FIX: Reset initialization guard AND all form/file state when navigating to a different report.
  // Without the state resets, old report data (media, fields) lingers until the new fetch resolves.
  useEffect(() => {
    // Update active report ref first — this causes any in-flight async compression
    // for the previous report to detect the change and discard its result
    activeReportIdRef.current = reportId;

    hasInitializedFiles.current = false;

    if (!editMode) {
      // Navigated to create-new — restore a clean slate immediately
      setFormData(initialFormData);
    }
    // In edit mode the populate effect below will fill the form once the report arrives;
    // reset files here so nothing from the previous report is ever visible.
    setExistingFiles([]);
    setNewFiles([]);
    setFilesToDelete([]);
    // Revoke any blob URLs from the previous session before clearing
    newFilePreviewUrlsRef.current.forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
    setNewFilePreviewUrls([]);
    // FIX: Clear upload queue so files queued for the previous report don't get
    // processed against the current report
    setUploadQueue([]);
    setIsProcessingQueue(false);
    // FIX #3: Reset submission guard on navigation
    setIsSubmitting(false);
  }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch comments and timeline when in edit mode
  useEffect(() => {
    if (editMode && reportId) {
      dispatch(fetchReportCommentsThunk(reportId));
      dispatch(fetchReportTimelineThunk(reportId));
    }
  }, [editMode, reportId, dispatch]);

  // Populate local state from existing report
  useEffect(() => {
    if (editMode && existingReport && !hasInitializedFiles.current) {
      hasInitializedFiles.current = true; // Only populate once — prevents Redux re-renders from resetting file state
      setFormData({
        incident_date: existingReport.incident_date || "",
        incident_time: existingReport.incident_time || null,
        store: existingReport.store?.id || "",
        details: existingReport.details || "",
        call_status: existingReport.call_status || "",
        reason_called: existingReport.reason_called || "",
        reason_not_called: existingReport.reason_not_called || "",
        severity: existingReport.severity || "",
        announcement_by: existingReport.announcement_by || null,
        customer_category: existingReport.customer_category || "",
        dress: existingReport.dress || "",
        product: existingReport.product || "",
        pickup_time: existingReport.pickup_time || null,
        hide_time: existingReport.hide_time || null,
        exit_time: existingReport.exit_time || null,
        status: existingReport.status || "SUBMITTED_REVIEW",
        incident_type: existingReport.incident_type || "theftPrevented",
      });

      if (existingReport.media_files && existingReport.media_files.length > 0) {
        setExistingFiles(existingReport.media_files);
      }
      setFilesToDelete([]);
    }
  }, [editMode, existingReport]);

  // ============ PROCESS UPLOAD QUEUE ============
  // This useEffect processes files from the queue one batch at a time
  // Prevents race conditions when multiple files are uploaded simultaneously
  useEffect(() => {
    const processQueue = async () => {
      // Don't process if already processing or queue is empty
      if (isProcessingQueue || uploadQueue.length === 0) return;

      // FIX: Snapshot the active report ID at the very start of this async job.
      // Every await point below checks this snapshot against the current ref value.
      // If the user navigates away mid-compression the ref will have changed and we
      // abort before any setState touches the new report's data.
      const jobReportId = activeReportIdRef.current;

      // Helper: returns true if the user has navigated away since this job started
      const isStale = () => activeReportIdRef.current !== jobReportId;

      // Helper: clean up all loading state and bail out of a stale job
      const abortStale = () => {
        console.warn('⛔ Aborting queue — report changed mid-compression (job was for:', jobReportId, ')');
        setIsProcessingQueue(false);
        setIsUploading(false);
        setIsCompressing(false);
        setCompressionProgress(0);
        setCompressionStatus('');
        setUploadProgress({});
      };

      console.log('🔄 Processing queue with', uploadQueue.length, 'files');
      setIsProcessingQueue(true);
      setIsUploading(true);

      // Take all files from queue
      const filesToProcess = [...uploadQueue];
      setUploadQueue([]); // Clear queue

      setIsCompressing(true);
      setCompressionProgress(0);

      const processedFiles = [];
      const initialProgress = {};
      const timestamp = Date.now(); // FIX: Generate timestamp once

      // Initialize progress for all files with consistent fileIds
      filesToProcess.forEach((file, index) => {
        const fileId = `${file.name}-${timestamp}-${index}`;
        initialProgress[fileId] = {
          file,
          progress: 0,
          completed: false,
          compressing: true
        };
      });
      setUploadProgress(initialProgress);

      for (let i = 0; i < filesToProcess.length; i++) {
        const file = filesToProcess[i];
        const fileType = file.type.split('/')[0];
        const fileId = `${file.name}-${timestamp}-${i}`; // FIX: Use same timestamp

        try {
          // Update status: Compressing
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { ...prev[fileId], progress: 10, compressing: true }
          }));

          let compressedFile;
          if (fileType === 'image') {
            setCompressionStatus(`Compressing image ${i + 1}/${filesToProcess.length}...`);
            compressedFile = await compressImage(file);
            // FIX: Image compression is also async — check for staleness after it resolves
            if (isStale()) { abortStale(); return; }
          } else if (fileType === 'video') {
            setCompressionStatus(`Compressing video ${i + 1}/${filesToProcess.length}... (may take a moment)`);
            compressedFile = await compressVideo(file);
            // compressVideo returns null when it detected the report changed internally
            if (compressedFile === null || isStale()) { abortStale(); return; }
          } else {
            compressedFile = file;
          }

          processedFiles.push(compressedFile);

          // Simulate upload progress after compression
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { ...prev[fileId], progress: 50, compressing: false }
          }));

          // Simulate upload completion
          await new Promise(resolve => setTimeout(resolve, 300));

          // FIX: Check staleness after every await, including the artificial delay
          if (isStale()) { abortStale(); return; }

          setUploadProgress(prev => ({
            ...prev,
            [fileId]: {
              file: compressedFile,
              progress: 100,
              completed: true,
              compressing: false
            }
          }));

        } catch (error) {
          console.error(`Failed to process ${file.name}:`, error);
          if (isStale()) { abortStale(); return; }
          processedFiles.push(file);

          setUploadProgress(prev => ({
            ...prev,
            [fileId]: {
              ...prev[fileId],
              progress: 100,
              completed: true,
              error: true,
              compressing: false
            }
          }));
        }

        const overallProgress = Math.round(((i + 1) / filesToProcess.length) * 100);
        if (fileType !== 'video') {
          setCompressionProgress(overallProgress);
        }
      }

      // FIX: Final staleness check — abort before touching ANY state if the report
      // changed while the last file was being processed
      if (isStale()) { abortStale(); return; }

      // Add all processed files at once - using functional update to ensure no race conditions
      setNewFiles((prev) => {
        console.log('✅ Adding files. Current:', prev.length, 'Adding:', processedFiles.length, 'New total:', prev.length + processedFiles.length);
        return [...prev, ...processedFiles];
      });

      // ============ NEW: Create preview URLs for each processed file ============
      setNewFilePreviewUrls((prevUrls) => {
        const newUrls = processedFiles.map((file) => URL.createObjectURL(file));
        return [...prevUrls, ...newUrls];
      });
      // ===========================================================================

      setIsCompressing(false);
      setCompressionProgress(0);
      setCompressionStatus('');

      // Clear upload progress after a short delay
      setTimeout(() => {
        setUploadProgress({});
        setIsUploading(false);
      }, 1500);

      setIsProcessingQueue(false);
    };

    processQueue();
  }, [uploadQueue, isProcessingQueue]);
  // =============================================

  // Handler for text / select changes
  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === "time" && value === "") {
      setFormData((prev) => ({ ...prev, [name]: null }));
    } else if (name === "announcement_by" && value === "") {
      setFormData((prev) => ({ ...prev, announcement_by: null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // NEW: Handle store selection from custom dropdown
  const handleStoreSelect = (storeId) => {
    setFormData((prev) => ({ ...prev, store: storeId }));
    setStoreDropdownOpen(false);
    setStoreSearch("");
  };

  // File handling
  // ============ MEDIA FILE NAMING HELPER ============
  // Generates a name like: MGS_theftPrevented_20240115_a3f.jpg
  // (first letters of store name words) + (incident type) + (date) + (short random suffix)
  const generateMediaFileName = (extension) => {
    // 1. Store initials — first letter of each word in the store name
    const storeName = selectedStore?.store_name || '';
    const storeInitials = storeName
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word[0].toUpperCase())
      .join('');

    // 2. Incident type — taken directly from formData (e.g. "theftPrevented")
    const incidentType = formData.incident_type || 'unknown';

    // 3. Date string — use the incident date if set, otherwise today
    const rawDate = formData.incident_date || new Date().toISOString().slice(0, 10);
    const dateStr = rawDate.replace(/-/g, ''); // "20240115"

    // 4. Short random suffix to avoid collisions between multiple uploads on the same day
    const suffix = Math.random().toString(36).slice(2, 5); // 3 chars, e.g. "a3f"

    const parts = [storeInitials, incidentType, dateStr, suffix].filter(Boolean);
    return `${parts.join('_')}.${extension}`;
  };
  // ==================================================

  // ============ IMAGE COMPRESSION ============
  const compressImage = async (file) => {
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        initialQuality: 0.8,
        fileType: 'image/jpeg'
      };


      const compressedBlob = await imageCompression(file, options);

      // Generate a structured filename: store initials + incident type + date + suffix
      const fileName = generateMediaFileName('jpg');
      const compressedFile = new File([compressedBlob], fileName, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });

      const originalMB = (file.size / 1024 / 1024).toFixed(2);
      const compressedMB = (compressedFile.size / 1024 / 1024).toFixed(2);
      const reduction = Math.round((1 - compressedFile.size / file.size) * 100);

      console.log(`📷 Image: ${originalMB}MB → ${compressedMB}MB (${reduction}% reduction) - ${fileName}`);
      return compressedFile;
    } catch (error) {
      console.error('Image compression error:', error);
      return file;
    }
  };

  // ============ VIDEO COMPRESSION ============
  const compressVideo = async (file) => {
    if (!ffmpegLoaded) {
      console.warn('⚠️ FFmpeg not loaded, skipping video compression');
      return file;
    }

    // Skip compression for files under 50MB (instant upload!) — still rename for consistency
    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB < 50) {
      console.log(`⚡ File under 50MB (${fileSizeMB.toFixed(2)}MB), skipping compression`);
      return new File([file], generateMediaFileName('mp4'), { type: file.type, lastModified: Date.now() });
    }

    try {
      // FIX: Use unique temp filenames per compression call.
      // Because file.name is always the same (e.g. "video.mp4") across all reports,
      // hardcoded names like 'input.mp4' would collide in FFmpeg's virtual filesystem
      // if two jobs overlap or the previous job's cleanup didn't finish in time.
      const uniqueId = Date.now();
      const inputName = `input_${uniqueId}.mp4`;
      const outputName = `output_${uniqueId}.mp4`;

      await ffmpeg.writeFile(inputName, await fetchFile(file));

      // ULTRA-FAST SETTINGS - Speed prioritized
      await ffmpeg.exec([
        '-i', inputName,
        '-vcodec', 'libx264',
        '-crf', '28',              // Faster encoding (higher = faster but lower quality)
        '-preset', 'ultrafast',    // Absolute fastest preset!
        '-vf', 'scale=-2:720',     // 720p for speed
        '-acodec', 'aac',
        '-b:a', '128k',            // Lower audio for speed
        '-movflags', '+faststart',
        '-y',
        outputName
      ]);

      const data = await ffmpeg.readFile(outputName);

      // FIX: If the user navigated to a different report while this video was
      // compressing, discard the result so it never lands in the wrong report's state
      if (activeReportIdRef.current !== reportId) {
        console.warn('⛔ Report changed during video compression — discarding stale result for:', file.name);
        await ffmpeg.deleteFile(inputName).catch(() => { });
        await ffmpeg.deleteFile(outputName).catch(() => { });
        return null; // Signals the queue processor to abort
      }

      const compressedBlob = new Blob([data.buffer], { type: 'video/mp4' });

      const compressedFile = new File(
        [compressedBlob],
        generateMediaFileName('mp4'),
        { type: 'video/mp4', lastModified: Date.now() }
      );

      const originalMB = (file.size / 1024 / 1024).toFixed(2);
      const compressedMB = (compressedFile.size / 1024 / 1024).toFixed(2);
      const reduction = Math.round((1 - compressedFile.size / file.size) * 100);

      console.log(`🎥 Video: ${originalMB}MB → ${compressedMB}MB (${reduction}% reduction) - ${compressedFile.name}`);

      await ffmpeg.deleteFile(inputName);
      await ffmpeg.deleteFile(outputName);

      return compressedFile;
    } catch (error) {
      console.error('❌ Video compression error:', error);
      return file;
    }
  };
  // ===========================================

  // ============ ENHANCED FILE HANDLER WITH QUEUE SYSTEM ============
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (selectedFiles.length === 0) return;

    // Add files to queue instead of processing immediately
    // This prevents race conditions when multiple uploads happen
    setUploadQueue(prev => {
      console.log('📥 Adding to queue:', selectedFiles.length, 'files');
      return [...prev, ...selectedFiles];
    });

    // Clear the input value to allow re-uploading the same file if needed
    e.target.value = '';
  };
  // ================================================================

  // handleUploadProgress function removed - now integrated into processQueue

  // ============ UPDATED: Delete handler must also clean preview URLs ============
  const handleNewFileDelete = (index) => {
    // Remove file
    setNewFiles((prevFiles) => {
      const updated = [...prevFiles];
      updated.splice(index, 1);
      return updated;
    });

    // Remove & revoke preview URL
    setNewFilePreviewUrls((prevUrls) => {
      const updated = [...prevUrls];
      const [urlToRevoke] = updated.splice(index, 1);
      if (urlToRevoke) {
        URL.revokeObjectURL(urlToRevoke);
      }
      return updated;
    });
  };
  // =============================================================================

  const handleExistingFileDelete = (fileId) => {
    console.log('Marking file for deletion:', fileId);

    setFilesToDelete(prev => {
      if (!prev.includes(fileId)) {
        const updated = [...prev, fileId];
        console.log('Updated files to delete:', updated);
        return updated;
      }
      return prev;
    });

    setExistingFiles(prev => {
      const updated = prev.filter(file => file.id !== fileId);
      console.log('Remaining existing files:', updated);
      return updated;
    });
  };

  // Reset form
  const handleReset = () => {
    showModal(
      'confirm',
      'Reset Form',
      'Are you sure you want to reset the form? All unsaved changes will be lost.',
      () => {
        setFormData(initialFormData);
        setNewFiles([]);
        if (editMode && existingReport) {
          setExistingFiles(existingReport.media_files || []);
          setFilesToDelete([]);
        } else {
          setExistingFiles([]);
          setFilesToDelete([]);
        }
      },
      'Reset',
      'Cancel'
    );
  };

  // Handle back button - works even when opened in a new tab
  const handleBack = () => {
    // Check if there's history to go back to
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // If no history (e.g., opened in new tab), go to dashboard
      navigate("/");
    }
  };

  // Toggle action menu
  const toggleActionMenu = () => {
    setActionMenuOpen(!actionMenuOpen);
  };

  // Handle adding comment
  const handleAddComment = () => {
    if (!newComment.trim()) {
      showModal('error', 'Error', 'Please enter a comment');
      return;
    }

    // Convert clean @Name mentions to backend format @[Name](id)
    let commentWithIds = newComment;
    mentionedEmployees.forEach(emp => {
      const cleanMention = `@${emp.name}`;
      const markdownMention = `@[${emp.name}](${emp.id})`;
      // Replace all occurrences of the clean mention with the markdown format
      commentWithIds = commentWithIds.split(cleanMention).join(markdownMention);
    });

    dispatch(addReportCommentThunk({ reportId, text: commentWithIds }))
      .unwrap()
      .then(() => {
        setNewComment("");
        setMentionedEmployees([]); // Clear mentioned employees
        showModal('success', 'Success', 'Comment added successfully');
      })
      .catch((err) => {
        showModal('error', 'Error', `Failed to add comment: ${err}`);
      });
  };

  // CREATE
  const handleSubmitCreate = (e) => {
    e.preventDefault();

    // FIX #3: Block if already submitting — prevents duplicate reports
    if (isSubmitting) return;

    // ============ NEW: Prevent submitting while uploads/compression are in progress ============
    if (isUploading || isProcessingQueue || isCompressing) {
      showModal(
        'error',
        'Please wait',
        'Files are still being processed/uploaded. Please wait until the upload completes.'
      );
      return;
    }
    // ===========================================================================================

    if (!employeeId) {
      showModal('error', 'Error', 'No employee profile associated with your account!');
      return;
    }

    // FIX #3: Lock immediately to prevent double-submission
    setIsSubmitting(true);

    const formDataToSend = new FormData();

    formDataToSend.append('employee_id', employeeId);
    formDataToSend.append('store_id', formData.store);
    formDataToSend.append('incident_date', formData.incident_date);
    formDataToSend.append('incident_time', formData.incident_time || '');
    formDataToSend.append('details', formData.details);
    formDataToSend.append('call_status', formData.call_status);
    formDataToSend.append('reason_called', formData.reason_called || '');
    formDataToSend.append('reason_not_called', formData.reason_not_called || '');
    formDataToSend.append('severity', formData.severity);
    formDataToSend.append('announcement_by', formData.announcement_by || '');
    formDataToSend.append('customer_category', formData.customer_category || '');
    formDataToSend.append('dress', formData.dress || '');
    formDataToSend.append('product', formData.product || '');
    formDataToSend.append('pickup_time', formData.pickup_time || '');
    formDataToSend.append('hide_time', formData.hide_time || '');
    formDataToSend.append('exit_time', formData.exit_time || '');
    formDataToSend.append('status', 'SUBMITTED_REVIEW');
    formDataToSend.append('incident_type', formData.incident_type);

    // ========== DIRECT-TO-S3 UPLOAD ==========
    // Big media no longer travels through the backend. We upload straight to
    // S3 first (with per-file progress), then create the report as a small
    // request, then attach the uploaded keys. If the backend doesn't have the
    // presign endpoints yet, we fall back to the legacy multipart path.
    (async () => {
      let s3Keys = [];
      let useLegacyPath = false;

      if (newFiles.length > 0) {
        try {
          setIsUploading(true);
          setUploadProgress({});
          s3Keys = await uploadFilesDirectToS3(newFiles, (fileName, percent) => {
            setUploadProgress((prev) => ({
              ...prev,
              [fileName]: {
                file: { name: fileName },
                progress: percent,
                completed: percent >= 100,
                compressing: false,
              },
            }));
          });
        } catch (err) {
          if (err instanceof PresignUnavailableError) {
            console.warn('Presign endpoint unavailable — falling back to legacy upload');
            useLegacyPath = true;
          } else {
            console.error('Direct S3 upload failed:', err);
            setIsUploading(false);
            setIsSubmitting(false);
            showModal('error', 'Upload failed', err.message || 'File upload failed. Please try again.');
            return;
          }
        } finally {
          setIsUploading(false);
          // Clear the progress panel: completed entries left the
          // "Uploading Files..." spinner on screen indefinitely.
          setUploadProgress({});
        }
      }

      if (useLegacyPath) {
        newFiles.forEach((file) => {
          formDataToSend.append('media_files', file);
        });
      }

      try {
        const created = await dispatch(createReportThunk(formDataToSend)).unwrap();

        // Attach the S3-uploaded media to the new report
        if (!useLegacyPath && s3Keys.length > 0) {
          try {
            await attachMediaToReport(created.id, s3Keys);
          } catch (attachErr) {
            // Report exists; media didn't attach — tell the user precisely.
            console.error('Failed to attach media:', attachErr);
            setIsSubmitting(false);
            showModal(
              'error',
              'Media attach failed',
              'Your report was created, but the uploaded files could not be linked to it. Please open the report and re-add the files.'
            );
            return;
          }
        }

        // FIX #2: Navigate immediately — no modal click required
        // replace:true removes the create page from history so Back doesn't return to it
        navigate("/", { replace: true });
      } catch (err) {
        console.error("Failed to create report:", err);
        // FIX #3: Unlock on failure so user can retry
        setIsSubmitting(false);
        showModal('error', 'Error', 'Failed to create report. Please try again.');
      }
    })();
  };

  // UPDATE
  const handleUpdate = (newStatus) => {
    if (!existingReport) return;

    const statusText = newStatus.replace(/_/g, " ");

    showModal(
      'confirm',
      'Confirm Action',
      `Are you sure you want to update this report with status: "${statusText}"?`,
      () => {
        // ============ NEW: Prevent updating while uploads/compression are in progress ============
        if (isUploading || isProcessingQueue || isCompressing) {
          showModal(
            'error',
            'Please wait',
            'Files are still being processed/uploaded. Please wait until the upload completes.'
          );
          return;
        }
        // =========================================================================================

        console.log('Starting update process...');
        console.log('Files to delete:', filesToDelete);
        console.log('New files to add:', newFiles);

        // FIXED: Only use FormData if we have files to delete or new files to upload
        // Otherwise, use regular JSON to avoid multipart/form-data issues
        const hasFilesToDelete = filesToDelete.length > 0;
        const hasNewFiles = newFiles.length > 0;
        const needsFormData = hasFilesToDelete || hasNewFiles;

        let dataToSend;

        if (needsFormData) {
          // Use FormData when we need to handle file operations
          const formDataToSend = new FormData();

          formDataToSend.append('employee', employeeId);
          formDataToSend.append('store', formData.store);
          formDataToSend.append('incident_date', formData.incident_date);
          formDataToSend.append('incident_time', formData.incident_time || '');
          formDataToSend.append('details', formData.details);
          formDataToSend.append('call_status', formData.call_status);
          formDataToSend.append('reason_called', formData.reason_called || '');
          formDataToSend.append('reason_not_called', formData.reason_not_called || '');
          formDataToSend.append('severity', formData.severity);
          formDataToSend.append('announcement_by', formData.announcement_by || '');
          formDataToSend.append('customer_category', formData.customer_category || '');
          formDataToSend.append('dress', formData.dress || '');
          formDataToSend.append('product', formData.product || '');
          formDataToSend.append('pickup_time', formData.pickup_time || '');
          formDataToSend.append('hide_time', formData.hide_time || '');
          formDataToSend.append('exit_time', formData.exit_time || '');
          formDataToSend.append('status', newStatus || formData.status);
          formDataToSend.append('incident_type', formData.incident_type);

          // New files now go DIRECTLY to S3 (see dispatch below) instead of
          // through this multipart request — they are appended here only if
          // the presign endpoint is unavailable (legacy fallback).

          // Only append files to delete if there are any
          if (hasFilesToDelete) {
            console.log('Adding files to delete to FormData:', filesToDelete);
            formDataToSend.append('files_to_delete', JSON.stringify(filesToDelete));

            filesToDelete.forEach((fileId, index) => {
              formDataToSend.append(`delete_file_${index}`, fileId);
            });

            formDataToSend.append('delete_files', filesToDelete.join(','));
          }

          console.log('Using FormData - contents:');
          for (let [key, value] of formDataToSend.entries()) {
            console.log(key, value);
          }

          dataToSend = formDataToSend;
        } else {
          // Use regular JSON when no file operations are needed
          dataToSend = {
            employee: employeeId,
            store: formData.store,
            incident_date: formData.incident_date,
            incident_time: formData.incident_time || '',
            details: formData.details,
            call_status: formData.call_status,
            reason_called: formData.reason_called || '',
            reason_not_called: formData.reason_not_called || '',
            severity: formData.severity,
            announcement_by: formData.announcement_by || '',
            customer_category: formData.customer_category || '',
            dress: formData.dress || '',
            product: formData.product || '',
            pickup_time: formData.pickup_time || '',
            hide_time: formData.hide_time || '',
            exit_time: formData.exit_time || '',
            status: newStatus || formData.status,
            incident_type: formData.incident_type,
          };

          console.log('Using JSON - data:', dataToSend);
        }

        // ========== DIRECT-TO-S3 UPLOAD (edit flow) ==========
        (async () => {
          let s3Keys = [];

          if (hasNewFiles) {
            try {
              setIsUploading(true);
              setUploadProgress({});
              s3Keys = await uploadFilesDirectToS3(newFiles, (fileName, percent) => {
                setUploadProgress((prev) => ({
                  ...prev,
                  [fileName]: {
                    file: { name: fileName },
                    progress: percent,
                    completed: percent >= 100,
                    compressing: false,
                  },
                }));
              });
            } catch (err) {
              if (err instanceof PresignUnavailableError) {
                console.warn('Presign endpoint unavailable — falling back to legacy upload');
                if (dataToSend instanceof FormData) {
                  newFiles.forEach((file) => dataToSend.append('media_files', file));
                }
              } else {
                console.error('Direct S3 upload failed:', err);
                setIsUploading(false);
                showModal('error', 'Upload failed', err.message || 'File upload failed. Please try again.');
                return;
              }
            } finally {
              setIsUploading(false);
              // Clear the progress panel: completed entries left the
              // "Uploading Files..." spinner on screen indefinitely.
              setUploadProgress({});
            }
          }

          const payload = {
            reportId: existingReport.id,
            updates: dataToSend,
          };

          try {
            const updatedReport = await dispatch(updateReportThunk(payload)).unwrap();
            console.log('Update successful:', updatedReport);

            if (s3Keys.length > 0) {
              try {
                const attachRes = await attachMediaToReport(existingReport.id, s3Keys);
                // FULL STATE SYNC: the files are now part of the report, so move
                // them from "New Files" into "Existing Files" immediately using
                // the authoritative URLs the backend just returned. Without this,
                // clearing newFiles made uploads invisible until a page reload.
                const attachedEntries = (attachRes?.attached || [])
                  .filter((a) => a && a.id && a.url)
                  .map((a) => ({ id: a.id, file: a.url }));
                if (attachedEntries.length > 0) {
                  setExistingFiles((prev) => [...prev, ...attachedEntries]);
                }
              } catch (attachErr) {
                console.error('Failed to attach media:', attachErr);
                showModal(
                  'error',
                  'Media attach failed',
                  'The report was updated, but the new files could not be linked to it. Please re-add them.'
                );
                return;
              }
            }

            setFilesToDelete([]);
            // The uploaded files now live in Existing Files (above) — finish the
            // hand-off: release preview blob URLs and clear the New Files card.
            newFilePreviewUrlsRef.current.forEach((url) => {
              try { URL.revokeObjectURL(url); } catch (e) { /* already revoked */ }
            });
            setNewFilePreviewUrls([]);
            setNewFiles([]);

            showModal(
              'success',
              'Success!',
              `Report updated successfully! Status: ${statusText}`,
              () => navigate("/"),
              'Continue'
            );
          } catch (err) {
            console.error("Failed to update report:", err);
            showModal('error', 'Error', `Failed to update report. Error: ${err.message || err}`);
          }
        })();
      },
      'Update',
      'Cancel'
    );
  };



  // PA users: do NOT block — they must be able to VIEW the report and add comments.
  // The form already handles PA correctly everywhere:
  //   • "Take Action" dropdown shows "no permission" when !userIsSPAOrAbove
  //   • All status-change buttons require userIsSPAOrAbove
  //   • form onSubmit is undefined in editMode so nothing can be accidentally saved
  // We show a soft info banner inside the header below instead of blocking.
  if (false && editMode && userIsPA) {
    return (
      <div className="incident-report-app">
        <div className="incident-report-content">
          <div className="incident-report-container">
            <div className="incident-report-error-message">
              <div style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #fecaca'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: '#fee2e2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h2 style={{ color: '#991b1b', marginBottom: '12px', fontSize: '24px' }}>
                  Access Denied
                </h2>
                <p style={{ color: '#7f1d1d', marginBottom: '24px', fontSize: '16px' }}>
                  PA users cannot edit reports. You can only add comments to reports.
                </p>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Go Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="incident-report-content">
      {/* Confirmation Modal */}
      {modalState.isOpen && (
        <div className="incident-report-modal-overlay">
          <div className="incident-report-modal">
            <div className="incident-report-modal-header">
              <div className={`incident-report-modal-icon incident-report-modal-icon-${modalState.type}`}>
                {modalState.type === 'success' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {modalState.type === 'error' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {modalState.type === 'confirm' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.228 9C8.337 7.8 9.22 7 10.5 7H13.5C14.78 7 15.663 7.8 15.772 9M8.228 9L8.228 9C8.228 9 8.228 9 8.228 9ZM8.228 9C8.337 10.2 9.22 11 10.5 11H13.5C14.78 11 15.663 10.2 15.772 9M15.772 9C15.772 9 15.772 9 15.772 9V9ZM15.772 9L15.772 9ZM7 9H17M10 3V7M14 3V7M7 9V20C7 20.5523 7.44772 21 8 21H16C16.5523 21 17 20.5523 17 20V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <h3 className="incident-report-modal-title">{modalState.title}</h3>
            </div>

            <div className="incident-report-modal-body">
              <p className="incident-report-modal-message">{modalState.message}</p>
            </div>

            <div className="incident-report-modal-footer">
              {modalState.type === 'confirm' && (
                <button
                  className="incident-report-modal-btn incident-report-modal-btn-secondary"
                  onClick={closeModal}
                >
                  {modalState.cancelText}
                </button>
              )}
              <button
                className={`incident-report-modal-btn ${modalState.type === 'error'
                  ? 'incident-report-modal-btn-danger'
                  : 'incident-report-modal-btn-primary'
                  }`}
                onClick={modalState.type === 'confirm' ? handleModalConfirm : closeModal}
              >
                {modalState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Store Notes Modal */}
      {showStoreNotesModal && (
        <div className="incident-report-modal-overlay" onClick={handleCloseStoreNotesModal}>
          <div className="incident-report-store-notes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incident-report-store-notes-header">
              <div>
                <h3>Store Notes</h3>
                {selectedStore && (
                  <p className="incident-report-store-notes-subtitle">
                    {selectedStore.store_name} - {selectedStore.store_city}
                  </p>
                )}
              </div>
              <button
                className="incident-report-store-notes-close"
                onClick={handleCloseStoreNotesModal}
              >
                Ã—
              </button>
            </div>

            <div className="incident-report-store-notes-body">
              {notesLoading ? (
                <div className="incident-report-store-notes-loading">
                  <div className="incident-report-loader"></div>
                  <p>Loading notes...</p>
                </div>
              ) : storeNotes && storeNotes.notes && storeNotes.notes.length > 0 ? (
                <div className="incident-report-store-notes-list">
                  {storeNotes.notes.map((note) => (
                    <div
                      key={note.id}
                      className={`incident-report-store-note-item ${note.is_important ? 'important' : ''
                        }`}
                    >
                      {note.is_important && (
                        <div className="incident-report-note-important-badge">
                          Important
                        </div>
                      )}
                      <p className="incident-report-note-text">{note.note_text}</p>
                      <div className="incident-report-note-meta">
                        <span>By: {note.created_by_name}</span>
                        <span>{new Date(note.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="incident-report-store-notes-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12H15M12 9V15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p>No notes available for this store</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="incident-report-layout">
        {/* Left side - Image */}
        <div className="incident-report-image-side">
          <div className="incident-report-image-content">
          </div>
          <button
            type="button"
            className="incident-report-back-btn"
            onClick={handleBack}
          >
            Back
          </button>
        </div>

        {/* Right side - Form */}
        <div className="incident-report-form-side">
          <div className="incident-report-container">
            <div className="incident-report-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0 }}>{editMode ? "Edit Incident Report" : "Create Incident Report"}</h1>
                {editMode && formData.status && (
                  <span
                    className={`incident-report-status-badge ${getStatusBadgeClass(formData.status)}`}
                    style={{ fontSize: '0.8rem', padding: '4px 12px', borderRadius: '20px', fontWeight: 600, letterSpacing: '0.3px' }}
                  >
                    {formData.status.replace(/_/g, " ")}
                  </span>
                )}
              </div>

              {/* PA User — read-only banner for view/comment mode */}
              {editMode && userIsPA && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px 16px',
                  backgroundColor: '#eff6ff',
                  border: '1px solid #93c5fd',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: '#2563eb', flexShrink: 0 }}>
                    <path d="M13 16h-1v-4h-1m1-4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p style={{ color: '#1e3a8a', fontWeight: '500', fontSize: '14px', margin: 0 }}>
                    You are viewing this report in read-only mode. You can add comments below but cannot change the report status or details.
                  </p>
                </div>
              )}

              {/* PA User Warning Banner for Create Mode */}
              {!editMode && userIsPA && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px 16px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: '#d97706', flexShrink: 0 }}>
                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: '#92400e', fontWeight: '500', fontSize: '14px', margin: 0 }}>
                      Note: As a PA user, you cannot upload media files with your reports.
                    </p>
                  </div>
                </div>
              )}

              {editMode && filesToDelete.length > 0 && (
                <div style={{ background: '#ffe6e6', padding: '8px', marginTop: '8px', borderRadius: '4px', fontSize: '12px' }}>
                  Files marked for deletion: {filesToDelete.join(', ')}
                </div>
              )}
            </div>

            <form onSubmit={editMode ? undefined : handleSubmitCreate}>
              {/* ... All existing form sections remain exactly the same ... */}
              {/* What's your incident like? */}
              <div className="incident-report-section">
                <h2>What's your incident like?</h2>

                <div className="incident-report-group">
                  <label htmlFor="incident_type">What type of incident is this?</label>
                  <div className="incident-report-select-wrapper">
                    <select
                      id="incident_type"
                      name="incident_type"
                      className="incident-report-select"
                      value={formData.incident_type}
                      onChange={handleChange}
                      required
                    >
                      <option value="theftPrevented">Theft Prevented</option>
                      <option value="theftReported">Theft Reported</option>
                      <option value="incidentReported">Incident Reported</option>
                      <option value="customerDenied">Customer Denied</option>
                      <option value="cashierSuspicious">Cashier Suspicious</option>
                      <option value="openingClosing">Opening/Closing</option>
                      <option value="announcement">Announcement</option>
                    </select>
                  </div>
                </div>

                <div className="incident-report-group">
                  <label htmlFor="incident_date">When did this incident occur?</label>
                  <input
                    id="incident_date"
                    type="date"
                    name="incident_date"
                    className="incident-report-date"
                    value={formData.incident_date}
                    onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div className="incident-report-group">
                  <label htmlFor="incident_time">At what time?</label>
                  <input
                    id="incident_time"
                    type="time"
                    name="incident_time"
                    className="incident-report-time"
                    value={formData.incident_time ?? ""}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Custom Store Selector with Search and Info Button */}
                <div className="incident-report-group">
                  <label htmlFor="store">
                    Where will your incident be located?
                    {formData.store && (
                      <button
                        type="button"
                        className="incident-report-store-info-btn"
                        onClick={handleShowStoreNotes}
                        title="View store notes"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                          <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        View Notes
                      </button>
                    )}
                  </label>

                  <div className="incident-report-custom-select" ref={storeDropdownRef}>
                    <div
                      className="incident-report-custom-select-trigger"
                      onClick={() => setStoreDropdownOpen(!storeDropdownOpen)}
                    >
                      <span>
                        {selectedStore
                          ? selectedStore.is_active === false
                            ? `${selectedStore.store_name} (Inactive)`
                            : selectedStore.store_name
                          : 'Select store/location'}
                      </span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={`incident-report-select-arrow ${storeDropdownOpen ? 'open' : ''}`}
                      >
                        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>

                    {storeDropdownOpen && (
                      <div className="incident-report-custom-select-dropdown">
                        <div className="incident-report-store-search">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                          <input
                            type="text"
                            placeholder="Search stores..."
                            value={storeSearch}
                            onChange={(e) => setStoreSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>

                        <div className="incident-report-store-options">
                          {storesLoading ? (
                            <div className="incident-report-store-option loading">
                              Loading stores...
                            </div>
                          ) : filteredStores.length > 0 ? (
                            filteredStores.map((store) => (
                              <div
                                key={store.id}
                                className={`incident-report-store-option ${formData.store === store.id ? 'selected' : ''
                                  } ${store.is_active === false ? 'inactive' : ''}`}
                                onClick={() => handleStoreSelect(store.id)}
                              >
                                <div className="incident-report-store-option-main">
                                  <span className="incident-report-store-name">{store.store_name}</span>
                                  {store.store_city && (
                                    <span className="incident-report-store-city">{store.store_city}</span>
                                  )}
                                  {store.is_active === false && (
                                    <span
                                      className="incident-report-store-inactive-badge"
                                      title="This store is currently inactive"
                                    >
                                      Inactive
                                    </span>
                                  )}
                                </div>
                                {formData.store === store.id && (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="incident-report-store-option empty">
                              No stores found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="incident-report-group">
                  <label htmlFor="severity">How severe was this incident?</label>
                  <div className="incident-report-select-wrapper">
                    <select
                      id="severity"
                      name="severity"
                      className="incident-report-select"
                      value={formData.severity}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select severity</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* How would you like to report? */}
              <div className="incident-report-section">
                <h2>How would you like to report?</h2>

                <div className="incident-report-group">
                  <label>Call Status:</label>
                  <div className="incident-report-radio-options">
                    <label className="incident-report-radio-option">
                      <input
                        type="radio"
                        name="call_status"
                        value="CALLED"
                        checked={formData.call_status === "CALLED"}
                        onChange={handleChange}
                        required
                      />
                      <div className="incident-report-radio-content">
                        <div className="incident-report-radio-check"></div>
                        <div>
                          <span className="incident-report-radio-title">Called</span>
                          <p className="incident-report-radio-description">The incident was reported via call</p>
                        </div>
                      </div>
                    </label>

                    <label className="incident-report-radio-option">
                      <input
                        type="radio"
                        name="call_status"
                        value="NOT_CALLED"
                        checked={formData.call_status === "NOT_CALLED"}
                        onChange={handleChange}
                      />
                      <div className="incident-report-radio-content">
                        <div className="incident-report-radio-check"></div>
                        <div>
                          <span className="incident-report-radio-title">Not Called</span>
                          <p className="incident-report-radio-description">The incident was not reported via call</p>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {formData.call_status === "CALLED" && (
                  <div className="incident-report-group">
                    <label htmlFor="reason_called">Reason for Called:</label>
                    <div className="incident-report-select-wrapper">
                      <select
                        id="reason_called"
                        name="reason_called"
                        className="incident-report-select"
                        value={formData.reason_called}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select reason</option>
                        {reasonCalledOptions.map((option, index) => (
                          <option value={option} key={index}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {formData.call_status === "NOT_CALLED" && (
                  <div className="incident-report-group">
                    <label htmlFor="reason_not_called">Reason for Not Called:</label>
                    <div className="incident-report-select-wrapper">
                      <select
                        id="reason_not_called"
                        name="reason_not_called"
                        className="incident-report-select"
                        value={formData.reason_not_called}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select reason</option>
                        {reasonNotCalledOptions.map((option, index) => (
                          <option value={option} key={index}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Details for High Severity */}
              {formData.severity === "high" && (
                <div className="incident-report-section">
                  <h2>Additional Details</h2>

                  <div className="incident-report-group">
                    <label>Customer Category:</label>
                    <div className="incident-report-radio-options">
                      <label className="incident-report-radio-option small">
                        <input
                          type="radio"
                          name="customer_category"
                          value="male"
                          checked={formData.customer_category === "male"}
                          onChange={handleChange}
                        />
                        <div className="incident-report-radio-content">
                          <div className="incident-report-radio-check"></div>
                          <span className="incident-report-radio-title">Male</span>
                        </div>
                      </label>

                      <label className="incident-report-radio-option small">
                        <input
                          type="radio"
                          name="customer_category"
                          value="female"
                          checked={formData.customer_category === "female"}
                          onChange={handleChange}
                        />
                        <div className="incident-report-radio-content">
                          <div className="incident-report-radio-check"></div>
                          <span className="incident-report-radio-title">Female</span>
                        </div>
                      </label>

                      <label className="incident-report-radio-option small">
                        <input
                          type="radio"
                          name="customer_category"
                          value="kid"
                          checked={formData.customer_category === "kid"}
                          onChange={handleChange}
                        />
                        <div className="incident-report-radio-content">
                          <div className="incident-report-radio-check"></div>
                          <span className="incident-report-radio-title">Kid</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="dress">Description of Dress:</label>
                    <input
                      type="text"
                      id="dress"
                      name="dress"
                      className="incident-report-input"
                      value={formData.dress}
                      onChange={handleChange}
                      placeholder="Color and type of clothing..."
                    />
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="product">Product Involved:</label>
                    <input
                      type="text"
                      id="product"
                      name="product"
                      className="incident-report-input"
                      value={formData.product}
                      onChange={handleChange}
                      placeholder="Product name or description..."
                    />
                  </div>

                  <div className="incident-report-group">
                    <label htmlFor="announcement_by">Announcement By:</label>
                    <div className="incident-report-select-wrapper">
                      <select
                        id="announcement_by"
                        name="announcement_by"
                        className="incident-report-select"
                        value={formData.announcement_by ?? ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Employee</option>
                        {employees.map((employee) => (
                          <option key={employee.id} value={employee.id}>
                            {employee.full_name || `${employee.user?.first_name || ''} ${employee.user?.last_name || ''}`.trim() || employee.name || `Employee #${employee.id}`}
                          </option>
                        ))}
                      </select>
                      {employeesLoading && <span className="incident-report-loading-indicator">Loading employees...</span>}
                    </div>
                  </div>

                  <div className="incident-report-timeline">
                    <h3>Incident Timeline</h3>
                    <div className="incident-report-timeline-fields">
                      <div className="incident-report-group">
                        <label htmlFor="pickup_time">Pickup Time:</label>
                        <input
                          id="pickup_time"
                          name="pickup_time"
                          type="time"
                          className="incident-report-time"
                          value={formData.pickup_time ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="incident-report-group">
                        <label htmlFor="hide_time">Hide Time:</label>
                        <input
                          id="hide_time"
                          name="hide_time"
                          type="time"
                          className="incident-report-time"
                          value={formData.hide_time ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="incident-report-group">
                        <label htmlFor="exit_time">Exit Time:</label>
                        <input
                          id="exit_time"
                          name="exit_time"
                          type="time"
                          className="incident-report-time"
                          value={formData.exit_time ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Incident Description */}
              <div className="incident-report-section">
                <h2>Incident Description</h2>
                <div className="incident-report-group">
                  <label htmlFor="details">Please describe what happened:</label>
                  <textarea
                    id="details"
                    name="details"
                    className="incident-report-textarea"
                    placeholder="Provide a detailed description of what happened..."
                    value={formData.details}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Evidence & Documentation — hidden for PA users (no upload permission) */}
              {!userIsPA && <div className="incident-report-section">
                <h2>Evidence & Documentation</h2>

                {/* Display existing files from server */}
                {existingFiles.length > 0 && (
                  <div className="incident-report-file-list">
                    <h3>Existing Files ({existingFiles.length})</h3>
                    <ul>
                      {existingFiles.map((file) => (
                        <li key={`existing-${file.id}`} className="incident-report-file-item">
                          <a
                            href={file.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="incident-report-file-link"
                          >
                            {getFileTypeFromUrl(file.file) === 'image' ? (
                              <img
                                src={file.file}
                                alt="existing file preview"
                                className="incident-report-file-preview"
                              />
                            ) : getFileTypeFromUrl(file.file) === 'video' ? (
                              <div className="incident-report-file-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M15 10L19.553 7.724C20.0049 7.49787 20.5256 7.43298 21.0197 7.54147C21.5138 7.64996 21.9501 7.9247 22.2472 8.31858C22.5443 8.71246 22.6837 9.19876 22.6399 9.68743C22.5961 10.1761 22.3721 10.6323 22.01 10.967L22 11V13C22 13.79 21.6 14.53 21 15L15 18.5V10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M2 7C2 5.89543 2.89543 5 4 5H13C14.1046 5 15 5.89543 15 7V17C15 18.1046 14.1046 19 13 19H4C2.89543 19 2 18.1046 2 17V7Z" stroke="currentColor" strokeWidth="2" />
                                </svg>
                              </div>
                            ) : (
                              <div className="incident-report-file-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M13 2V9H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>
                            )}
                            <span className="incident-report-file-name">
                              {getFileNameFromUrl(file.file)}
                            </span>
                          </a>
                          <button
                            type="button"
                            className="incident-report-delete-file"
                            onClick={() => handleExistingFileDelete(file.id)}
                            aria-label="Delete file"
                            title={`Delete file (ID: ${file.id})`}
                          >
                            &times;
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* File upload for new files */}
                <div className="incident-report-file-container">

                  {/* ============ QUEUE DISPLAY ============ */}
                  {uploadQueue.length > 0 && !isProcessingQueue && (
                    <div style={{
                      padding: '12px',
                      marginBottom: '16px',
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #22c55e',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#22c55e',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {uploadQueue.length}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#15803d'
                        }}>
                          {uploadQueue.length} file{uploadQueue.length > 1 ? 's' : ''} queued
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#166534',
                          marginTop: '2px'
                        }}>
                          Will be processed after current compression completes
                        </div>
                      </div>
                    </div>
                  )}
                  {/* ======================================= */}

                  {/* ============ COMPRESSION PROGRESS INDICATOR ============ */}
                  {isCompressing && (
                    <div style={{
                      padding: '12px',
                      marginBottom: '16px',
                      backgroundColor: '#f0f9ff',
                      border: '1px solid #0ea5e9',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '3px solid #e0f2fe',
                        borderTop: '3px solid #0ea5e9',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#0369a1',
                          marginBottom: '4px'
                        }}>
                          {compressionStatus}
                        </div>
                        {compressionProgress > 0 && (
                          <div style={{
                            width: '100%',
                            height: '6px',
                            backgroundColor: '#e0f2fe',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${compressionProgress}%`,
                              height: '100%',
                              backgroundColor: '#0ea5e9',
                              transition: 'width 0.3s ease'
                            }}></div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* ======================================================== */}                  {/* File Upload Section - Available for SPA+ in both create and edit modes */}
                  {userIsPA ? (
                    /* PA Users: Show warning - cannot upload in any mode */
                    <div style={{
                      padding: '20px',
                      backgroundColor: '#fef3c7',
                      border: '1px solid #fbbf24',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'start',
                      gap: '12px'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, color: '#d97706' }}>
                        <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div>
                        <p style={{ color: '#92400e', fontWeight: '500', marginBottom: '4px' }}>
                          File uploads not available
                        </p>
                        <p style={{ color: '#78350f', fontSize: '14px' }}>
                          PA users cannot upload media files. Please fill in all other required fields.
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* SPA and above: Show file upload in both create and edit modes */
                    <label className="incident-report-file-upload">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="incident-report-file-input"
                      />
                      <div className="incident-report-upload-content">
                        <div className="incident-report-upload-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 16.5V7.5M12 7.5L8.5 11M12 7.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.5" />
                          </svg>
                        </div>
                        <div className="incident-report-upload-text">
                          <span>Upload {existingFiles.length > 0 ? 'additional ' : ''}files</span>
                          <p>Click to browse or drag files here</p>
                        </div>
                      </div>
                    </label>
                  )}
                </div>

                {/* Upload Progress Indicators */}
                {Object.keys(uploadProgress).length > 0 && (
                  <div className="incident-report-upload-progress-section">
                    <h3>Uploading Files...</h3>
                    <div className="incident-report-progress-list">
                      {Object.entries(uploadProgress).map(([fileId, data]) => {
                        // Progress entries historically had two shapes: rich objects
                        // from the compression path and BARE NUMBERS from the
                        // direct-to-S3 onProgress callback. The number shape crashed
                        // this renderer (data.file.name on undefined). Both callbacks
                        // now write the object shape, and this renderer tolerates
                        // any shape so a future writer can never crash the form.
                        const pct = typeof data === 'number' ? data : (data?.progress ?? 0);
                        return (
                        <div key={fileId} className="incident-report-progress-item">
                          <div className="incident-report-progress-info">
                            <span className="incident-report-progress-filename">
                              {data?.file?.name ?? fileId}
                            </span>
                            <span className="incident-report-progress-percentage">
                              {Math.round(pct)}%
                            </span>
                          </div>
                          <div className="incident-report-progress-bar">
                            <div
                              className="incident-report-progress-fill"
                              style={{ width: `${pct}%` }}
                            ></div>
                          </div>
                          {data?.completed && (
                            <div className="incident-report-progress-complete">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <span>Complete</span>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Display newly uploaded files */}
                {newFiles.length > 0 && (
                  <div className="incident-report-file-list">
                    <h3>New Files ({newFiles.length})</h3>
                    <ul>
                      {newFiles.map((file, index) => {
                        const previewUrl = newFilePreviewUrls[index];

                        return (
                          <li key={`new-${index}`} className="incident-report-file-item">
                            {previewUrl ? (
                              <a
                                href={previewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="incident-report-file-link"
                                download={file.name}
                              >
                                {file.type?.startsWith("image/") ? (
                                  <img
                                    src={previewUrl}
                                    alt={file.name || "preview"}
                                    className="incident-report-file-preview"
                                  />
                                ) : file.type?.startsWith("video/") ? (
                                  <div className="incident-report-file-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M15 10L19.553 7.724C20.0049 7.49787 20.5256 7.43298 21.0197 7.54147C21.5138 7.64996 21.9501 7.9247 22.2472 8.31858C22.5443 8.71246 22.6837 9.19876 22.6399 9.68743C22.5961 10.1761 22.3721 10.6323 22.01 10.967L22 11V13C22 13.79 21.6 14.53 21 15L15 18.5V10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M2 7C2 5.89543 2.89543 5 4 5H13C14.1046 5 15 5.89543 15 7V17C15 18.1046 14.1046 19 13 19H4C2.89543 19 2 18.1046 2 17V7Z" stroke="currentColor" strokeWidth="2" />
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="incident-report-file-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M13 2V9H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </div>
                                )}
                                <span className="incident-report-file-name">
                                  {file.name}
                                </span>
                              </a>
                            ) : (
                              <div className="incident-report-file-link">
                                <div className="incident-report-file-icon">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M13 2V9H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </div>
                                <span className="incident-report-file-name">
                                  {file.name}
                                </span>
                              </div>
                            )}

                            <button
                              type="button"
                              className="incident-report-delete-file"
                              onClick={() => handleNewFileDelete(index)}
                              aria-label="Delete file"
                              title={`Delete file: ${file.name}`}
                            >
                              &times;
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>}

              {/* Comments & Activity Log Section - Only visible in edit mode */}
              {editMode && (
                <div className="incident-report-section">

                  {/* ── Tab bar ─────────────────────────────────────────────── */}
                  <div className="incident-report-tabs">
                    <button
                      type="button"
                      className={`incident-report-tab ${activeTab === 'comments' ? 'incident-report-tab--active' : ''}`}
                      onClick={() => setActiveTab('comments')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Comments
                      {commentsState.comments.length > 0 && (
                        <span className="incident-report-tab-badge">{commentsState.comments.length}</span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`incident-report-tab ${activeTab === 'timeline' ? 'incident-report-tab--active' : ''}`}
                      onClick={() => setActiveTab('timeline')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Activity Log
                      {timelineState.count > 0 && (
                        <span className="incident-report-tab-badge">{timelineState.count}</span>
                      )}
                    </button>
                  </div>

                  {/* ── COMMENTS TAB ──────────────────────────────────────── */}
                  {activeTab === 'comments' && (
                    <>
                      {/* Add new comment with mention support */}
                      <div className="incident-report-comment-input-container">
                        <div className="incident-report-comment-input-wrapper">
                          <div className="incident-report-mention-wrapper">
                            <textarea
                              ref={commentInputRef}
                              className="incident-report-comment-input"
                              placeholder="Add a comment... (Type @ to mention someone)"
                              value={newComment}
                              onChange={handleCommentChange}
                              rows="3"
                            />

                            {/* Mention dropdown */}
                            {showMentionDropdown && (
                              <div
                                ref={mentionDropdownRef}
                                className="incident-report-mention-dropdown"
                              >
                                <div className="incident-report-mention-dropdown-header">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                  <span>Select employee to mention</span>
                                </div>
                                <div className="incident-report-mention-list">
                                  {filteredEmployeesForMention.length > 0 ? (
                                    filteredEmployeesForMention.map((emp) => (
                                      <div
                                        key={emp.id}
                                        className="incident-report-mention-item"
                                        onClick={() => handleMentionSelect(emp)}
                                      >
                                        {emp.image ? (
                                          <img
                                            src={emp.image}
                                            alt={emp.user?.first_name || emp.name}
                                            className="incident-report-mention-avatar"
                                          />
                                        ) : (
                                          <div className="incident-report-mention-avatar-placeholder">
                                            {emp.user?.first_name?.charAt(0) || emp.name?.charAt(0) || 'U'}
                                          </div>
                                        )}
                                        <div className="incident-report-mention-info">
                                          <span className="incident-report-mention-name">
                                            {`${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim() || emp.name || `Employee #${emp.id}`}
                                          </span>
                                          {emp.role && (
                                            <span className="incident-report-mention-role">
                                              {emp.role}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="incident-report-mention-empty">
                                      No employees found
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            className="incident-report-comment-submit"
                            onClick={handleAddComment}
                            disabled={addingComment || !newComment.trim()}
                          >
                            {addingComment ? 'Adding...' : 'Add Comment'}
                          </button>
                        </div>
                        {addCommentError && (
                          <div className="incident-report-comment-error">
                            {addCommentError}
                          </div>
                        )}
                      </div>

                      {/* Display comments with mentions */}
                      <div className="incident-report-comments-list">
                        {commentsState.loading && (
                          <div className="incident-report-comments-loading">
                            <div className="incident-report-loader"></div>
                            <p>Loading comments...</p>
                          </div>
                        )}

                        {commentsState.error && (
                          <div className="incident-report-comments-error">
                            Failed to load comments: {commentsState.error}
                          </div>
                        )}

                        {!commentsState.loading && commentsState.comments.length === 0 && (
                          <div className="incident-report-no-comments">
                            <p>No comments yet. Be the first to comment!</p>
                          </div>
                        )}

                        {!commentsState.loading && commentsState.comments.length > 0 && (
                          <div className="incident-report-comments">
                            {commentsState.comments.map((comment) => (
                              <div key={comment.id} className="incident-report-comment">
                                <div className="incident-report-comment-header">
                                  <div className="incident-report-comment-author">
                                    {comment.commented_by.image ? (
                                      <img
                                        src={comment.commented_by.image}
                                        alt={comment.commented_by.name}
                                        className="incident-report-comment-avatar"
                                      />
                                    ) : (
                                      <div className="incident-report-comment-avatar-placeholder">
                                        {comment.commented_by.name.charAt(0)}
                                      </div>
                                    )}
                                    <div className="incident-report-comment-author-info">
                                      <span className="incident-report-comment-author-name">
                                        {comment.commented_by.name}
                                      </span>
                                      {comment.commented_by.role && (
                                        <span className="incident-report-comment-author-role">
                                          {comment.commented_by.role}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span className="incident-report-comment-date">
                                    {new Date(comment.created_at).toLocaleString()}
                                  </span>
                                </div>
                                <div className="incident-report-comment-body">
                                  {parseCommentText(comment.text).map((part, index) => {
                                    if (part.type === 'mention') {
                                      return (
                                        <span key={index} className="incident-report-comment-mention">
                                          @{part.name}
                                        </span>
                                      );
                                    }
                                    return <span key={index}>{part.content}</span>;
                                  })}
                                </div>
                                {comment.mentioned_employees && comment.mentioned_employees.length > 0 && (
                                  <div className="incident-report-comment-mentions-list">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span>Mentioned: {comment.mentioned_employees.map(e => e.name).join(', ')}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* ── ACTIVITY LOG TAB ──────────────────────────────────── */}
                  {activeTab === 'timeline' && (
                    <div className="incident-report-timeline">
                      {timelineState.loading && (
                        <div className="incident-report-comments-loading">
                          <div className="incident-report-loader"></div>
                          <p>Loading activity log...</p>
                        </div>
                      )}

                      {timelineState.error && (
                        <div className="incident-report-comments-error">
                          Failed to load activity log: {timelineState.error}
                        </div>
                      )}

                      {!timelineState.loading && timelineState.entries.length === 0 && (
                        <div className="incident-report-timeline-empty">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="#9ca3af" strokeWidth="1.5" />
                            <path d="M12 6V12L16 14" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <p>No activity recorded for this report yet.</p>
                        </div>
                      )}

                      {!timelineState.loading && timelineState.entries.length > 0 && (
                        <div className="incident-report-timeline-list">
                          {timelineState.entries.map((entry, idx) => {
                            const actorName = entry.performed_by_name || 'System';
                            const actorRole = entry.performed_by_role || '';
                            const actorImage = entry.performed_by_image || null;

                            // Pick icon + colour by action_type
                            const typeConfig = {
                              CREATED: {
                                color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" /></svg>
                                )
                              },
                              STATUS_CHANGED: {
                                color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M4 12H20M14 6L20 12L14 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                )
                              },
                              UPDATED: {
                                color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                )
                              },
                              COMMENTED: {
                                color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', icon: (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                )
                              },
                              FILE_ADDED: {
                                color: '#0891b2', bg: '#f0f9ff', border: '#bae6fd', icon: (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M13 2V9H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                )
                              },
                            };
                            const cfg = typeConfig[entry.action_type] || typeConfig.UPDATED;
                            const isLast = idx === timelineState.entries.length - 1;

                            return (
                              <div key={entry.id} className="incident-report-timeline-item">
                                {/* Vertical connector line */}
                                {!isLast && <div className="incident-report-timeline-connector" />}

                                {/* Ring dot */}
                                <div
                                  className="incident-report-timeline-dot"
                                  style={{
                                    border: `2.5px solid ${cfg.border}`,
                                    boxShadow: `0 0 0 4px #f9fafb, 0 0 0 5px ${cfg.border}`,
                                    color: cfg.color,
                                  }}
                                >
                                  {cfg.icon}
                                </div>

                                {/* Card */}
                                <div className="incident-report-timeline-card">
                                  <div className="incident-report-timeline-card-header">
                                    {/* Actor */}
                                    <div className="incident-report-timeline-actor">
                                      {actorImage ? (
                                        <img src={actorImage} alt={actorName} className="incident-report-timeline-avatar" />
                                      ) : (
                                        <div
                                          className="incident-report-timeline-avatar-placeholder"
                                          style={{ backgroundColor: cfg.bg, color: cfg.color }}
                                        >
                                          {actorName.charAt(0).toUpperCase()}
                                        </div>
                                      )}
                                      <div className="incident-report-timeline-actor-info">
                                        <span className="incident-report-timeline-actor-name">{actorName}</span>
                                        {actorRole && (
                                          <span className="incident-report-timeline-actor-role">{actorRole}</span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Action badge + time */}
                                    <div className="incident-report-timeline-meta">
                                      <span
                                        className="incident-report-timeline-badge"
                                        style={{ backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
                                      >
                                        {entry.action_type_display || entry.action_type}
                                      </span>
                                      <span className="incident-report-timeline-time">
                                        {new Date(entry.timestamp).toLocaleString(undefined, {
                                          day: '2-digit', month: '2-digit', year: 'numeric',
                                          hour: '2-digit', minute: '2-digit',
                                        })}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="incident-report-timeline-divider" />

                                  {/* Summary */}
                                  <p className="incident-report-timeline-summary">{entry.summary}</p>

                                  {/* Status transition */}
                                  {entry.old_status_display && entry.new_status_display && (
                                    <div className="incident-report-timeline-status-change">
                                      <span className="incident-report-timeline-status incident-report-timeline-status--old">
                                        {entry.old_status_display}
                                      </span>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                        <path d="M4 12H20M14 6L20 12L14 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                      <span className="incident-report-timeline-status incident-report-timeline-status--new">
                                        {entry.new_status_display}
                                      </span>
                                    </div>
                                  )}

                                  {/* Changed fields */}
                                  {entry.changed_fields && Object.keys(entry.changed_fields).length > 0 && (
                                    <div className="incident-report-timeline-fields">
                                      {Object.entries(entry.changed_fields).map(([field, [oldVal, newVal]]) => (
                                        <div key={field} className="incident-report-timeline-field-row">
                                          <span className="incident-report-timeline-field-name">{field.replace(/_/g, ' ')}</span>
                                          <span className="incident-report-timeline-field-old">{oldVal || '—'}</span>
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                            <path d="M4 12H20M14 6L20 12L14 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                          <span className="incident-report-timeline-field-new">{newVal || '—'}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              )}

              {/* Form Actions */}
              <div className="incident-report-actions">
                {!editMode ? (
                  <>
                    <button
                      type="button"
                      className="incident-report-btn-secondary"
                      onClick={handleReset}
                      disabled={isUploading || isProcessingQueue || isCompressing || isSubmitting}
                    >
                      Reset Form
                    </button>
                    <div className="incident-report-right-actions">
                      <button
                        type="submit"
                        className="incident-report-btn-primary"
                        disabled={isUploading || isProcessingQueue || isCompressing || isSubmitting}
                      >
                        {isSubmitting
                          ? 'Creating Report...'
                          : (isUploading || isProcessingQueue || isCompressing)
                            ? 'Processing Files...'
                            : 'Create Report'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="incident-report-status-actions">
                      <div className="incident-report-status-display">
                        <strong>Current Status:</strong>
                        <span className={`incident-report-status-badge ${getStatusBadgeClass(formData.status)}`}>
                          {formData.status.replace(/_/g, " ")}
                        </span>
                      </div>

                      <div className="incident-report-action-dropdown" ref={actionMenuRef}>
                        <button
                          type="button"
                          className="incident-report-action-button incident-report-take-action-btn"
                          onClick={userIsPA ? undefined : toggleActionMenu}
                          disabled={isUploading || isProcessingQueue || isCompressing || userIsPA}
                          style={userIsPA ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
                        >
                          {(isUploading || isProcessingQueue || isCompressing) ? 'Processing Files...' : 'Take Action'}
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={`incident-report-dropdown-arrow ${actionMenuOpen ? 'open' : ''}`}
                          >
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>

                        {actionMenuOpen && !(isUploading || isProcessingQueue || isCompressing) && (
                          <div className="incident-report-dropdown-menu">

                            {/* ① Approve — Team Manager and above (rank >= 5) */}
                            {userIsTeamManagerOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-approve"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                onClick={() => { handleUpdate("APPROVED"); setActionMenuOpen(false); }}
                              >
                                <CheckCircle size={15} strokeWidth={2.2} style={{ flexShrink: 0 }} />
                                Approve
                              </button>
                            )}

                            {/* ② Submit for Review — SPA and above (FIX #1) */}
                            {userIsSPAOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-submit"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                onClick={() => { handleUpdate("SUBMITTED_REVIEW"); setActionMenuOpen(false); }}
                              >
                                <FileText size={14} strokeWidth={2.2} style={{ flexShrink: 0 }} />
                                Submit for Review
                              </button>
                            )}

                            {/* ③ Submit for Approval — SPA and above */}
                            {userIsSPAOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-submit"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                onClick={() => { handleUpdate("SUBMITTED_APPROVAL"); setActionMenuOpen(false); }}
                              >
                                <Send size={14} strokeWidth={2.2} style={{ flexShrink: 0 }} />
                                Submit for Approval
                              </button>
                            )}

                            {/* ③ Decline — SPA and above */}
                            {userIsSPAOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-decline"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                onClick={() => { handleUpdate("DECLINED"); setActionMenuOpen(false); }}
                              >
                                <XCircle size={15} strokeWidth={2.2} style={{ flexShrink: 0 }} />
                                Decline
                              </button>
                            )}

                            {/* ④ Mark Discrepancy — SPA and above */}
                            {userIsSPAOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-warning"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                onClick={() => { handleUpdate("DISCREPANCY"); setActionMenuOpen(false); }}
                              >
                                <AlertTriangle size={14} strokeWidth={2.2} style={{ flexShrink: 0 }} />
                                Mark Discrepancy
                              </button>
                            )}

                            {/* ⑤ Update (Keep Status) — SPA and above */}
                            {userIsSPAOrAbove && (
                              <button
                                type="button"
                                className="incident-report-dropdown-item incident-report-dropdown-update"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                onClick={() => { handleUpdate(formData.status); setActionMenuOpen(false); }}
                              >
                                <RefreshCw size={14} strokeWidth={2.2} style={{ flexShrink: 0 }} />
                                Update (Keep Status)
                              </button>
                            )}

                            {/* No permission message */}
                            {!userIsSPAOrAbove && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', color: '#6b7280', fontSize: '14px' }}>
                                <LockKeyhole size={14} style={{ flexShrink: 0 }} />
                                You don't have permission to take actions on this report.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateEditReportForm;